import { useCallback, useMemo, useState } from "react";

import { UsersResponse } from "src/gen/schema/brig/bapi";
import { User as Bot } from "src/gen/schema/models/user";

import { AddBotModal } from "../addBotModal";
import { DeleteBotModal } from "../deleteBotModal";
import { EditBotModal } from "../editBotModal";

type AddBotModalPayload = {
  readonly openAddBotModal: () => void;
  readonly addBotModal: React.ReactElement | null;
};

export type SuccessCallback = (response?: UsersResponse) => void;

export const useAddBot = (
  handleSuccess: SuccessCallback
): AddBotModalPayload => {
  const [open, setOpen] = useState<boolean>(false);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const addBotModal = useMemo(() => {
    if (open) {
      return (
        <AddBotModal
          show={open}
          onSuccess={handleSuccess}
          onClose={handleClose}
        />
      );
    }
    return null;
  }, [open, handleClose, handleSuccess]);

  const openAddBotModal = useCallback((): void => {
    setOpen(true);
  }, []);

  return {
    openAddBotModal,
    addBotModal
  };
};

type EditBotPayload = {
  openEditBotModal: (bot: Bot) => void;
  editBotModal: React.ReactElement | null;
};

export const useEditBot = (handleSuccess: SuccessCallback): EditBotPayload => {
  const [bot, setBot] = useState<Bot>();

  const editBotModal = useMemo(() => {
    const handleClose = () => {
      setBot(undefined);
    };
    if (bot) {
      return (
        <EditBotModal
          bot={bot}
          onSuccess={handleSuccess}
          onClose={handleClose}
          show={bot !== undefined}
        />
      );
    }
    return null;
  }, [bot, handleSuccess]);

  return {
    openEditBotModal: setBot,
    editBotModal
  };
};

type DeleteBotPayload = {
  openDeleteBotModal: (bot: Bot) => void;
  deleteBotModal: React.ReactElement | null;
};

export const useDeleteBot = (
  handleSuccess: SuccessCallback
): DeleteBotPayload => {
  const [bot, setBot] = useState<Bot>();

  const deleteBotModal = useMemo(() => {
    const handleClose = () => {
      setBot(undefined);
    };
    if (bot) {
      return (
        <DeleteBotModal
          botId={bot.id}
          botName={bot.email}
          onSuccess={handleSuccess}
          onClose={handleClose}
          show={bot !== undefined}
        />
      );
    }
    return null;
  }, [bot, handleSuccess]);

  return {
    openDeleteBotModal: setBot,
    deleteBotModal
  };
};
